import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

const FEEDBACK_CONTEXT_CF = 'customfield_10047';

export interface AIFeedbackProperties {
	entrypointId: string;
	locale: string;
	userId?: string;
	displayName?: string;
	feedbackContextId: string;
	feedbackContext: string;
}

type AIFeedbackPropertiesContextType = AIFeedbackProperties | undefined;

export const AIFeedbackPropertiesContext =
	createContext<AIFeedbackPropertiesContextType>(undefined);

type AIFeedbackPropertiesProviderProps = {
	children: ReactNode;
	entrypointId: string;
	locale?: string;
	userId?: string;
	displayName?: string;
	feedbackContextId?: string;
	feedbackContext?: object;
};

export const AIFeedbackPropertiesProvider = ({
	children,
	entrypointId,
	locale,
	feedbackContextId = FEEDBACK_CONTEXT_CF,
	feedbackContext = {},
	userId,
	displayName,
}: AIFeedbackPropertiesProviderProps) => {
	const { locale: intlLocale } = useIntl();

	const feedbackContextString = Object.entries(feedbackContext)
		.map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
		.join(' ');

	const contextValue = useMemo<AIFeedbackProperties>(
		() => ({
			entrypointId,
			feedbackContextId,
			feedbackContext: feedbackContextString,
			locale: locale || intlLocale,
			userId,
			displayName,
		}),
		[
			entrypointId,
			feedbackContextId,
			feedbackContextString,
			locale,
			intlLocale,
			userId,
			displayName,
		],
	);

	return (
		<AIFeedbackPropertiesContext.Provider value={contextValue}>
			{children}
		</AIFeedbackPropertiesContext.Provider>
	);
};

export const useAIFeedbackProperties = (): AIFeedbackProperties => {
	const context = useContext(AIFeedbackPropertiesContext);

	if (context === undefined) {
		throw new Error('useAIFeedbackProperties must be used within a AIFeedbackPropertiesProvider');
	}

	return context;
};
